import React from "react";
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    addDays,
    isSameMonth,
    isSameDay,
    compareAsc,
} from "date-fns";
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import DayView from "./DayView";
import { CalendarProps } from "../../types/calendar-props";
import PostCard from "./PostCard";

interface MonthlyViewProps extends CalendarProps {
    currentDate: Date;
}

const MonthlyView: React.FC<MonthlyViewProps> = ({
    posts,
    currentDate,
    loading,
    handleMoreClick,
    closeDayView,
    selectedDay,
}) => {
    // Calculate the start and end dates for the calendar grid
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 }); // Sunday

    // Generate an array of dates for the calendar grid
    const totalDays = [];
    let day = calendarStart;
    while (compareAsc(day, calendarEnd) <= 0) {
        totalDays.push(day);
        day = addDays(day, 1);
    }

    // Split the days into weeks (each sub-array contains 7 dates)
    const weeks = [];
    for (let i = 0; i < totalDays.length; i += 7) {
        weeks.push(totalDays.slice(i, i + 7));
    }

    const renderDayCell = (day: Date) => {
        const dayPosts = posts
            ?.filter((post) => isSameDay(new Date(post.scheduledFor), day))
            ?.sort((a, b) =>
                compareAsc(new Date(a.scheduledFor), new Date(b.scheduledFor))
            );

        // Show up to 2 posts
        const postsToShow = dayPosts?.slice(0, 2);
        const hasMorePosts = dayPosts?.length > 2;

        const isToday = isSameDay(day, new Date());
        const isCurrentMonth = isSameMonth(day, currentDate);

        return (
            <div
                key={day.toString()}
                className="p-1 border border-gray-200 relative"
                style={{
                    backgroundColor: isToday
                        ? "rgba(240, 223, 175, 0.50)"
                        : isCurrentMonth
                        ? "transparent"
                        : "#F0F0F0",
                    // Adjust minimum height as necessary for your design
                    minHeight: (window.innerHeight - 265) / 5,
                }}
            >
                <div className="absolute top-2 left-2 text-xs font-semibold">
                    {format(day, "d")}
                </div>
                <div className="mt-6 space-y-1">
                    {postsToShow?.map((post, index) => (
                        <PostCard
                            date={format(day, "EEE-d")}
                            idx={index}
                            key={post.id}
                            post={post}
                        />
                    ))}
                    {hasMorePosts && (
                        <div
                            className="flex justify-center items-center cursor-pointer mt-2"
                            onClick={() => handleMoreClick(day)}
                        >
                            <HiOutlineDotsHorizontal
                                size={20}
                                className="text-primary"
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Header row labels for days of the week
    const weekDays = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
    ];

    return (
        <>
            {/* Overflow container enables horizontal scrolling on smaller screens */}
            <div className="overflow-x-auto">
                {/* The container has a minimum width to always display 7 columns */}
                <div className="w-full min-w-[700px]">
                    {/* Header row as a grid */}
                    <div className="grid grid-cols-7">
                        {weekDays.map((day, index) => (
                            <div
                                key={index}
                                className="p-1 border border-gray-200 border-b-secondary text-center font-bold"
                            >
                                {day}
                            </div>
                        ))}
                    </div>
                    {/* Calendar weeks as grid rows */}
                    {weeks.map((week, index) => (
                        <div key={index} className="grid grid-cols-7">
                            {week.map(renderDayCell)}
                        </div>
                    ))}
                </div>
            </div>
            {selectedDay && (
                <DayView
                    day={selectedDay}
                    posts={posts}
                    onClose={closeDayView}
                    loading={loading}
                />
            )}
        </>
    );
};

export default MonthlyView;
